import { KeyPairAndSalt } from "@/types/cryptography";
import { KEY_PAIR_STORE } from "@/utils/hooks/useKeys/constants";
import { deleteAllKeysFromSessionStorage, getItemFromSessionStorage, getKeysFromSessionStorage } from "@/utils/hooks/useKeys/utils";
import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
type RefetchFn = () => Promise<{
  user: KeyPairAndSalt | null;
  org: KeyPairAndSalt | null;
  orgSecret: string | null;
}>;
export type KeyPairContextType = {
  user: KeyPairAndSalt | null;
  org: KeyPairAndSalt | null;
  orgSecret: string | null;
  // True while a password submission is in flight. Drives the Unlock button.
  isLoading: boolean;
  // False until the mount-time read of sessionStorage has settled.
  isReady: boolean;
  setLoading: (prop: boolean) => void;
  refetch: RefetchFn;
  reset: () => void;
};
type KeyPair = {
  user: KeyPairAndSalt | null;
  org: KeyPairAndSalt | null;
  orgSecret: string | null;
};
const refetchKeys = async () => {
  console.log("Refetching key pairs from SessionStorage");
  try {
    return {
      user: await getKeysFromSessionStorage(KEY_PAIR_STORE.user),
      org: await getKeysFromSessionStorage(KEY_PAIR_STORE.org),
      orgSecret: await getItemFromSessionStorage(KEY_PAIR_STORE.orgSecret)
    };
  } catch (e) {
    console.error("Error while refetching key pairs from SessionStorage", e);
    console.log("Deleting SessionStorage");
    deleteAllKeysFromSessionStorage();
  }
  return {
    user: null,
    org: null,
    orgSecret: null
  };
};
export const KeyPairContext = createContext<KeyPairContextType | undefined>(undefined);
const defaultState = {
  user: null,
  org: null,
  orgSecret: null
};
export const KeyPairProvider = ({
  children
}: PropsWithChildren) => {
  const [keyPair, setKeyPair] = useState<KeyPair>(defaultState);
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const reset = useCallback(() => {
    setKeyPair(defaultState);
  }, []);
  const setLoading = useCallback((prop: boolean) => setIsLoading(prop), []);
  const refetch: RefetchFn = useCallback(async () => {
    const keys = await refetchKeys();
    setKeyPair(keys);
    return keys;
  }, []);
  const keyPairValues = useMemo(() => ({
    user: keyPair?.user || null,
    org: keyPair?.org || null,
    orgSecret: keyPair?.orgSecret || null
  }), [keyPair.user, keyPair.org, keyPair.orgSecret]);
  useEffect(() => {
    let mounted = true;
    refetch().finally(() => {
      if (mounted) setIsReady(true);
    });
    return () => {
      mounted = false;
    };
  }, [refetch]);
  const value = useMemo(() => ({
    ...keyPairValues,
    isLoading,
    isReady,
    setLoading,
    refetch,
    reset
  }), [keyPairValues, isLoading, isReady, setLoading, refetch, reset]);
  return <KeyPairContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="KeyPairProvider" data-sentry-source-file="KeyPairProvider.tsx">{children}</KeyPairContext.Provider>;
};